import React, { useState } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Save, Loader2, Palette, Bell, Globe, Check } from 'lucide-react';
import { toast } from 'sonner';

const Preferences = () => {
  const { theme, toggleTheme } = useTheme();
  const [preferences, setPreferences] = useState({
    theme: theme || 'dark',
    language: 'en',
    notifications: {
      email: true,
      push: false,
      lowStock: true,
      newOrders: true,
      priceChanges: false
    },
    dateFormat: 'MM/DD/YYYY',
    timeZone: 'America/Detroit',
    currency: 'USD'
  });
  const [loading, setLoading] = useState(false);

  const handleSave = async () => {
    setLoading(true);
    try {
      // Apply theme change
      if (preferences.theme !== theme) {
        toggleTheme();
      }
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 500));
      
      toast.success('Preferences saved successfully!');
    } catch (error) {
      console.error('Error saving preferences:', error);
      toast.error('Error saving preferences');
    } finally {
      setLoading(false);
    }
  };

  const handleNotificationChange = (key) => {
    setPreferences({
      ...preferences,
      notifications: {
        ...preferences.notifications,
        [key]: !preferences.notifications[key]
      }
    });
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6 pb-8">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Preferences</h1>
        <p className="text-gray-500 dark:text-gray-400 mt-1">
          Customize your ZuperBooks experience
        </p>
      </div>

      {/* Appearance Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Palette className="h-5 w-5 text-purple-600" />
            <CardTitle>Appearance</CardTitle>
          </div>
          <CardDescription>Customize how ZuperBooks looks</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Theme
            </label>
            <select
              value={preferences.theme}
              onChange={(e) => setPreferences({ ...preferences, theme: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 
                       rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            >
              <option value="light">Light</option>
              <option value="dark">Dark</option>
              <option value="auto">Auto (System)</option>
            </select>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Choose how ZuperBooks appears. Auto will match your system settings.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Language
            </label>
            <select
              value={preferences.language}
              onChange={(e) => setPreferences({ ...preferences, language: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 
                       rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            >
              <option value="en">English (US)</option>
              <option value="es">Español</option>
              <option value="fr">Français</option>
              <option value="de">Deutsch</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Notifications Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Bell className="h-5 w-5 text-blue-600" />
            <CardTitle>Notifications</CardTitle>
          </div>
          <CardDescription>Manage how you receive notifications</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-3">
            <label className="flex items-center justify-between p-3 rounded-lg 
                           hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors cursor-pointer">
              <div>
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  Email Notifications
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Receive notifications via email
                </p>
              </div>
              <div className="relative inline-block w-12 h-6">
                <input
                  type="checkbox"
                  checked={preferences.notifications.email}
                  onChange={() => handleNotificationChange('email')}
                  className="sr-only peer"
                />
                <div className="w-12 h-6 bg-gray-300 dark:bg-gray-600 rounded-full peer 
                              peer-checked:bg-blue-600 transition-colors"></div>
                <div className="absolute left-1 top-1 w-4 h-4 bg-white rounded-full 
                              peer-checked:translate-x-6 transition-transform"></div>
              </div>
            </label>

            <label className="flex items-center justify-between p-3 rounded-lg 
                           hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors cursor-pointer">
              <div>
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  Low Stock Alerts
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Get notified when inventory is running low
                </p>
              </div>
              <div className="relative inline-block w-12 h-6">
                <input
                  type="checkbox"
                  checked={preferences.notifications.lowStock}
                  onChange={() => handleNotificationChange('lowStock')}
                  className="sr-only peer"
                />
                <div className="w-12 h-6 bg-gray-300 dark:bg-gray-600 rounded-full peer 
                              peer-checked:bg-blue-600 transition-colors"></div>
                <div className="absolute left-1 top-1 w-4 h-4 bg-white rounded-full 
                              peer-checked:translate-x-6 transition-transform"></div>
              </div>
            </label>

            <label className="flex items-center justify-between p-3 rounded-lg 
                           hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors cursor-pointer">
              <div>
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  New Order Notifications
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Alert when new purchase orders are created
                </p>
              </div>
              <div className="relative inline-block w-12 h-6">
                <input
                  type="checkbox"
                  checked={preferences.notifications.newOrders}
                  onChange={() => handleNotificationChange('newOrders')}
                  className="sr-only peer"
                />
                <div className="w-12 h-6 bg-gray-300 dark:bg-gray-600 rounded-full peer 
                              peer-checked:bg-blue-600 transition-colors"></div>
                <div className="absolute left-1 top-1 w-4 h-4 bg-white rounded-full 
                              peer-checked:translate-x-6 transition-transform"></div>
              </div>
            </label>

            <label className="flex items-center justify-between p-3 rounded-lg 
                           hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors cursor-pointer">
              <div>
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  Price Changes
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Notify when product prices change
                </p>
              </div>
              <div className="relative inline-block w-12 h-6">
                <input
                  type="checkbox"
                  checked={preferences.notifications.priceChanges}
                  onChange={() => handleNotificationChange('priceChanges')}
                  className="sr-only peer"
                />
                <div className="w-12 h-6 bg-gray-300 dark:bg-gray-600 rounded-full peer 
                              peer-checked:bg-blue-600 transition-colors"></div>
                <div className="absolute left-1 top-1 w-4 h-4 bg-white rounded-full 
                              peer-checked:translate-x-6 transition-transform"></div>
              </div>
            </label>
          </div>
        </CardContent>
      </Card>

      {/* Regional Settings Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Globe className="h-5 w-5 text-green-600" />
            <CardTitle>Regional Settings</CardTitle>
          </div>
          <CardDescription>Configure regional preferences</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Date Format
              </label>
              <select
                value={preferences.dateFormat}
                onChange={(e) => setPreferences({ ...preferences, dateFormat: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 
                         rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
              >
                <option value="MM/DD/YYYY">MM/DD/YYYY (US)</option>
                <option value="DD/MM/YYYY">DD/MM/YYYY (European)</option>
                <option value="YYYY-MM-DD">YYYY-MM-DD (ISO)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Currency
              </label>
              <select
                value={preferences.currency}
                onChange={(e) => setPreferences({ ...preferences, currency: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 
                         rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
              >
                <option value="USD">USD - US Dollar ($)</option>
                <option value="EUR">EUR - Euro (€)</option>
                <option value="GBP">GBP - British Pound (£)</option>
                <option value="CAD">CAD - Canadian Dollar (C$)</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Time Zone
            </label>
            <select
              value={preferences.timeZone}
              onChange={(e) => setPreferences({ ...preferences, timeZone: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 
                       rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            >
              <option value="America/Detroit">America/Detroit (EST)</option>
              <option value="America/New_York">America/New_York (EST)</option>
              <option value="America/Chicago">America/Chicago (CST)</option>
              <option value="America/Denver">America/Denver (MST)</option>
              <option value="America/Los_Angeles">America/Los_Angeles (PST)</option>
              <option value="Europe/London">Europe/London (GMT)</option>
              <option value="Europe/Paris">Europe/Paris (CET)</option>
              <option value="Asia/Tokyo">Asia/Tokyo (JST)</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          disabled={loading}
          className="gap-2"
        >
          {loading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="h-4 w-4" />
              Save Preferences
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default Preferences;
